import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../utils/apiBase";
import { useProjects } from "../../../utils/projects";
import {
  AreaChart,
  BarList,
  ChartCard,
  ColumnChart,
  Legend,
  StackedBarList,
  StatTile,
  formatNumber,
  formatPercent,
  makeProjectColors,
} from "./charts";
import "./overview.css";

// Local calendar date as YYYY-MM-DD (not UTC, so "today" is today in India)
const localDay = (date) =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const daysAgo = (n) => localDay(new Date(Date.now() - n * 86400000));

const PRESETS = [
  { key: "30d", label: "Last 30 days", days: 30, period: "vs previous 30 days" },
  { key: "90d", label: "Last 90 days", days: 90, period: "vs previous 90 days" },
  { key: "6m", label: "Last 6 months", days: 183, period: "vs previous 6 months" },
  { key: "12m", label: "Last 12 months", days: 365, period: "vs previous 12 months" },
  { key: "all", label: "All time", days: null, period: "" },
];

const rangeFor = (preset) =>
  preset.days == null
    ? { start: "2000-01-01", end: localDay(new Date()) }
    : { start: daysAgo(preset.days - 1), end: localDay(new Date()) };

const bucketLabels = (key, unit) => {
  const date = new Date(`${key}T00:00:00`);
  if (unit === "month") {
    return {
      label: date.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
      fullLabel: date.toLocaleDateString("en-IN", { month: "long", year: "numeric" }),
    };
  }
  const short = date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  const long = date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return { label: short, fullLabel: unit === "week" ? `Week of ${long}` : long };
};

const prettyDay = (day) =>
  new Date(`${day}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// "Everything in one picture": headline numbers, the visit trend, and the
// breakdowns — all scoped by one date-range filter row at the top.
const OverviewView = () => {
  const [presetKey, setPresetKey] = useState("12m"); // recent months have the data
  const [range, setRange] = useState(() => rangeFor(PRESETS[3]));
  const [custom, setCustom] = useState(() => rangeFor(PRESETS[3]));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { projects } = useProjects();
  const { order: projectOrder, colorOf } = useMemo(
    () => makeProjectColors(projects.map((p) => p.name)),
    [projects]
  );

  const load = useCallback(async ({ start, end }) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/v1/allData/overview?start=${start}&end=${end}`
      );
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || "Could not load the overview");
      setData(body.data);
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(range);
  }, [range, load]);

  const choosePreset = (preset) => {
    setPresetKey(preset.key);
    const next = rangeFor(preset);
    setCustom(next);
    setRange(next);
  };

  const applyCustom = () => {
    if (!custom.start || !custom.end || custom.start > custom.end) {
      toast.error("Choose a start date on or before the end date");
      return;
    }
    setPresetKey("custom");
    setRange({ ...custom });
  };

  const preset = PRESETS.find((p) => p.key === presetKey);
  const periodLabel = preset ? preset.period : "vs previous period";
  const kpis = data?.kpis;
  const visitors = kpis?.visitors.value || 0;
  const visits = kpis?.visits.value || 0;

  const timeline = (data?.timeline || []).map((bucket) => ({
    ...bucketLabels(bucket.key, data.range.unit),
    value: bucket.total,
    breakdown: Object.entries(bucket.byProject)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value),
  }));

  const rangeText =
    data && data.range.start > "2000-01-01"
      ? `${prettyDay(data.range.start)} – ${prettyDay(data.range.end)}`
      : "All recorded visits";

  // Projects that actually appear in a top-N list, in fixed legend order
  const projectsIn = (group) =>
    projectOrder.filter((project) => (group?.items || []).some((item) => item.byProject?.[project]));
  const partnerProjects = projectsIn(data?.topChannelPartners);
  const salesProjects = projectsIn(data?.topSales);

  // "11 Sept 2025 – 10 Sept 2026 · top 8 of 140"
  const topSubtitle = (lead, group) =>
    `${lead} · ${rangeText}${
      group?.others ? ` · top ${group.items.length} of ${group.items.length + group.others}` : ""
    }`;
  const byProjectText = (row) =>
    projectOrder
      .filter((p) => row.byProject?.[p])
      .map((p) => `${p}: ${row.byProject[p]}`)
      .join(", ");

  return (
    <div className={`overview${loading ? " is-loading" : ""}`}>
      {/* One filter row, above everything it scopes */}
      <div className="ov-filters" role="group" aria-label="Date range">
        <div className="ov-presets">
          {PRESETS.map((p) => (
            <button
              type="button"
              key={p.key}
              className={`ov-chip${presetKey === p.key ? " is-active" : ""}`}
              aria-pressed={presetKey === p.key}
              onClick={() => choosePreset(p)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="ov-custom">
          <label>
            <span>From</span>
            <input
              type="date"
              value={custom.start > "2000-01-01" ? custom.start : ""}
              onChange={(e) => setCustom((c) => ({ ...c, start: e.target.value }))}
            />
          </label>
          <label>
            <span>To</span>
            <input
              type="date"
              value={custom.end}
              onChange={(e) => setCustom((c) => ({ ...c, end: e.target.value }))}
            />
          </label>
          <button type="button" className="ov-apply" onClick={applyCustom}>
            Apply
          </button>
        </div>
      </div>

      <div className="ov-heading">
        <h2>Overview</h2>
        <p>{data ? rangeText : "Loading…"}</p>
      </div>

      {kpis && (
        <div className="ov-kpis">
          <StatTile
            label="Site visits"
            value={formatNumber(visits)}
            delta={{ value: visits, previous: kpis.visits.previous, periodLabel }}
          />
          <StatTile
            label="Unique visitors"
            value={formatNumber(visitors)}
            delta={{ value: visitors, previous: kpis.visitors.previous, periodLabel }}
          />
          <StatTile
            label="Repeat visitors"
            value={formatNumber(kpis.repeatVisitors.value)}
            sub={visitors ? `${formatPercent(kpis.repeatVisitors.value / visitors)} of visitors came back` : undefined}
          />
          <StatTile
            label="Via channel partner"
            value={formatPercent(kpis.cpShare.value)}
            sub={`${formatNumber(kpis.cpShare.cpVisitors)} of ${formatNumber(visitors)} visitors`}
          />
          <StatTile
            label="New channel partners"
            value={formatNumber(kpis.newChannelPartners.value)}
            delta={{
              value: kpis.newChannelPartners.value,
              previous: kpis.newChannelPartners.previous,
              periodLabel,
            }}
          />
        </div>
      )}

      {data && visits === 0 ? (
        <div className="ov-empty">
          <strong>No site visits in this range.</strong>
          <span>Pick a longer range, such as Last 12 months or All time, to see earlier visits.</span>
        </div>
      ) : (
        data && (
          <div className="ov-grid">
            <ChartCard
              className="ov-span-2"
              title="Site visits over time"
              subtitle={`Per ${data.range.unit} · hover or use arrow keys for the project breakdown`}
              table={{
                columns: ["Period", "Visits", "By project"],
                rows: timeline.map((p) => [
                  p.fullLabel,
                  formatNumber(p.value),
                  p.breakdown.map((b) => `${b.label}: ${b.value}`).join(", ") || "—",
                ]),
              }}
            >
              <AreaChart points={timeline} valueLabel="visits" colorOf={colorOf} />
            </ChartCard>

            <ChartCard
              title="Visits by project"
              subtitle={`Each project's share of ${formatNumber(visits)} visits`}
              table={{
                columns: ["Project", "Visits", "Share"],
                rows: data.byProject.map((r) => [r.label, formatNumber(r.value), formatPercent(r.value / visits)]),
              }}
            >
              <BarList rows={data.byProject} total={visits} unit="visits" colorOf={colorOf} showShare />
            </ChartCard>

            <ChartCard
              title="Budget"
              subtitle="Visitors by budget band"
              table={{ columns: ["Budget", "Visitors"], rows: data.budget.map((r) => [r.label, formatNumber(r.value)]) }}
            >
              <ColumnChart rows={data.budget} total={visitors} unit="visitors" />
            </ChartCard>

            <ChartCard
              title="Requirement"
              subtitle="Visitors by size"
              table={{ columns: ["Requirement", "Visitors"], rows: data.requirement.map((r) => [r.label, formatNumber(r.value)]) }}
            >
              <ColumnChart rows={data.requirement} total={visitors} unit="visitors" />
            </ChartCard>

            <ChartCard
              title="Top sales persons"
              subtitle={topSubtitle("Visitors met, split by project", data.topSales)}
              table={{
                columns: ["Sales person", "Visitors", "By project"],
                rows: data.topSales.items.map((r) => [r.label, formatNumber(r.value), byProjectText(r)]),
              }}
            >
              {data.topSales.items.length ? (
                <>
                  <Legend items={salesProjects} colorOf={colorOf} />
                  <StackedBarList rows={data.topSales.items} order={projectOrder} colorOf={colorOf} />
                </>
              ) : (
                <p className="ov-none">No sales-person meetings recorded in this range.</p>
              )}
            </ChartCard>

            <ChartCard
              className="ov-span-2"
              title="Top channel partners"
              subtitle={topSubtitle("Visitors brought, split by project", data.topChannelPartners)}
              table={{
                columns: ["Channel partner", "Visitors", "By project"],
                rows: data.topChannelPartners.items.map((r) => [r.label, formatNumber(r.value), byProjectText(r)]),
              }}
            >
              {data.topChannelPartners.items.length ? (
                <>
                  <Legend items={partnerProjects} colorOf={colorOf} />
                  <StackedBarList rows={data.topChannelPartners.items} order={projectOrder} colorOf={colorOf} />
                </>
              ) : (
                <p className="ov-none">No channel-partner visitors in this range.</p>
              )}
            </ChartCard>
          </div>
        )
      )}
    </div>
  );
};

export default OverviewView;
